import { supabase } from '../supabaseClient';
import type { Speicher } from '../verortung/sitzung';
import { setzeKatalogHaeufigkeit } from './store';
import type { AufgeloesterKatalog, KatalogHaeufigkeit } from './typen';

/**
 * Reihenfolge nach Häufigkeit (Betreiber 28.09.: „anhand der Häufigkeit
 * ausgewählter Mängel soll sich die Anordnung automatisch verbessern“ —
 * firmenweit, alle drei Ebenen).
 *
 * - Zählung: RPC `mangel_katalog_haeufigkeit` (Nr. 132) liefert je
 *   (katalog_key, katalog_kategorie) ein Gewicht über die Mängel der eigenen
 *   Firma; jüngere zählen mehr. Einmal je Anmeldung im Hintergrund geladen,
 *   der letzte Stand liegt als Spiegel im localStorage (offline, schneller
 *   Start). Fehlt die RPC, kein Netz, Unsinn → keine Umsortierung.
 * - Sortierung: häufig Gewähltes rückt nach vorn, sonst gilt die Reihenfolge
 *   des Admins. Damit die Torte nicht bei jeder Kleinigkeit umspringt
 *   (Positionen sind Muskelgedächtnis), zählen nur Stufen: ab
 *   HAEUFIGKEIT_SCHWELLE und dann je Verdopplung eine Stufe; innerhalb einer
 *   Stufe bleibt die Admin-Reihenfolge.
 * - Die Auswahl nimmt den Stand beim Öffnen (MangelKatalogAuswahl) — nichts
 *   springt unter dem Finger.
 */

export const HAEUFIGKEIT_RPC = 'mangel_katalog_haeufigkeit';
/** Ab diesem Gewicht (≈ zwei frische Mängel) rückt ein Knoten vor. */
export const HAEUFIGKEIT_SCHWELLE = 2;
export const HAEUFIGKEIT_SPIEGEL_SCHLUESSEL = 'sigeko.mangelKatalog.haeufigkeit.v1';
/** Obergrenze der Zeilen (wie `limit` der RPC). */
export const HAEUFIGKEIT_MAX_ZEILEN = 3000;
/** Wie die Längengrenze der Spalten (Nr. 131). */
const MAX_ID = 120;

const idLesen = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const s = v.trim();
    return s && s.length <= MAX_ID ? s : null;
};

const gewichtLesen = (v: unknown): number | null => {
    const g = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
    return Number.isFinite(g) && g > 0 ? g : null;
};

const leer = (h: KatalogHaeufigkeit): boolean => h.knoten.size === 0 && h.kategorien.size === 0;

/**
 * Zeilen der RPC → Häufigkeit. Gleiche IDs werden summiert (ein Knoten kann
 * mit verschiedenen Kategorien vorkommen, wenn der Admin umgehängt hat).
 * Kein Array → null; kaputte Zeilen fallen weg. Nie werfen.
 */
export function haeufigkeitNormalisieren(roh: unknown): KatalogHaeufigkeit | null {
    if (!Array.isArray(roh)) return null;
    const knoten = new Map<string, number>();
    const kategorien = new Map<string, number>();
    for (const z of roh.slice(0, HAEUFIGKEIT_MAX_ZEILEN)) {
        if (!z || typeof z !== 'object') continue;
        const o = z as Record<string, unknown>;
        const g = gewichtLesen(o.gewicht);
        if (g === null) continue;
        const k = idLesen(o.katalog_key);
        const kat = idLesen(o.katalog_kategorie);
        if (k) knoten.set(k, (knoten.get(k) ?? 0) + g);
        if (kat) kategorien.set(kat, (kategorien.get(kat) ?? 0) + g);
    }
    return { knoten, kategorien };
}

/**
 * Stufe eines Gewichts: unter der Schwelle -1 (bleibt, wo der Admin ihn
 * hingestellt hat), sonst ⌊log₂ Gewicht⌋ — ein Knoten überholt einen
 * anderen erst, wenn er etwa doppelt so oft gewählt wurde.
 */
export function haeufigkeitsStufe(gewicht: number): number {
    return Number.isFinite(gewicht) && gewicht >= HAEUFIGKEIT_SCHWELLE ? Math.floor(Math.log2(gewicht)) : -1;
}

/**
 * Gewicht je sichtbarem Knoten: Mangel = eigene Zählung; Gruppe = Summe
 * darunter; Kategorie = das Größere aus Summe darunter und Kategorie-Zählung
 * (die enthält auch Treffer der Suche und Freitext in der Kategorie).
 */
export function knotenGewichte(katalog: AufgeloesterKatalog, h: KatalogHaeufigkeit): Map<string, number> {
    const gewichte = new Map<string, number>();
    const besucht = new Set<string>();
    const rechnen = (id: string): number => {
        const fertig = gewichte.get(id);
        if (fertig !== undefined) return fertig;
        if (besucht.has(id)) return 0;
        besucht.add(id);
        const k = katalog.knoten.get(id);
        if (!k) return 0;
        let g = h.knoten.get(id) ?? 0;
        for (const kind of katalog.kinder.get(id) ?? []) g += rechnen(kind);
        if (k.typ === 'kategorie') g = Math.max(g, h.kategorien.get(id) ?? 0);
        gewichte.set(id, g);
        return g;
    };
    for (const id of katalog.kinder.get('') ?? []) rechnen(id);
    return gewichte;
}

const sortiertCache = new WeakMap<AufgeloesterKatalog, WeakMap<KatalogHaeufigkeit, AufgeloesterKatalog>>();

/**
 * Katalog mit Kindern nach Häufigkeitsstufe (absteigend), innerhalb einer
 * Stufe in der bisherigen Reihenfolge (Admin/Standard). Gleiche Knoten, nur
 * `kinder` neu; ohne Wirkung dieselbe Referenz. Gemerkt je Katalog und
 * Häufigkeit, damit Folge-Caches (WeakMap je Katalog) stabil bleiben.
 */
export function nachHaeufigkeitSortieren(katalog: AufgeloesterKatalog, h: KatalogHaeufigkeit | null | undefined): AufgeloesterKatalog {
    if (!h || leer(h)) return katalog;
    const jeKatalog = sortiertCache.get(katalog);
    const vorhanden = jeKatalog?.get(h);
    if (vorhanden) return vorhanden;

    const gewichte = knotenGewichte(katalog, h);
    const kinder = new Map<string, readonly string[]>();
    let geaendert = false;
    for (const [eltern, ids] of katalog.kinder) {
        const stufen = ids.map((id, i) => ({ id, i, s: haeufigkeitsStufe(gewichte.get(id) ?? 0) }));
        if (!stufen.some((x) => x.s >= 0)) {
            kinder.set(eltern, ids);
            continue;
        }
        const neu = [...stufen].sort((a, b) => b.s - a.s || a.i - b.i).map((x) => x.id);
        if (neu.every((id, i) => id === ids[i])) kinder.set(eltern, ids);
        else {
            kinder.set(eltern, neu);
            geaendert = true;
        }
    }
    const ergebnis: AufgeloesterKatalog = geaendert ? { ...katalog, kinder } : katalog;
    const karte = jeKatalog ?? new WeakMap<KatalogHaeufigkeit, AufgeloesterKatalog>();
    karte.set(h, ergebnis);
    if (!jeKatalog) sortiertCache.set(katalog, karte);
    return ergebnis;
}

// ─── Laden ───────────────────────────────────────────────────────────────────

export type HaeufigkeitAbfrage = () => PromiseLike<{ data: unknown; error: unknown }>;

const standardAbfrage: HaeufigkeitAbfrage = () => supabase.rpc(HAEUFIGKEIT_RPC);

/** Fragt die RPC; Fehler (RPC fehlt, kein Netz), Unsinn → null. Wirft nie. */
export async function haeufigkeitAbrufen(abfrage: HaeufigkeitAbfrage = standardAbfrage): Promise<KatalogHaeufigkeit | null> {
    try {
        const { data, error } = await abfrage();
        if (error) return null;
        return haeufigkeitNormalisieren(data);
    } catch {
        return null;
    }
}

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const paareLesen = (roh: unknown): Map<string, number> => {
    const m = new Map<string, number>();
    if (!Array.isArray(roh)) return m;
    for (const p of roh.slice(0, HAEUFIGKEIT_MAX_ZEILEN)) {
        if (!Array.isArray(p)) continue;
        const id = idLesen(p[0]);
        const g = gewichtLesen(p[1]);
        if (id && g !== null) m.set(id, g);
    }
    return m;
};

/** Spiegel des letzten erfolgreichen Ladens — nur für denselben Nutzer. */
export function haeufigkeitSpiegelLesen(nutzerId: string, speicher: Speicher | null): KatalogHaeufigkeit | null {
    if (!speicher || !nutzerId) return null;
    try {
        const roh: unknown = JSON.parse(speicher.getItem(HAEUFIGKEIT_SPIEGEL_SCHLUESSEL) ?? 'null');
        if (!roh || typeof roh !== 'object') return null;
        const o = roh as Record<string, unknown>;
        if (o.nutzerId !== nutzerId) return null;
        const h = { knoten: paareLesen(o.knoten), kategorien: paareLesen(o.kategorien) };
        return leer(h) ? null : h;
    } catch {
        return null;
    }
}

export function haeufigkeitSpiegelSchreiben(nutzerId: string, h: KatalogHaeufigkeit, speicher: Speicher | null): void {
    if (!speicher || !nutzerId) return;
    try {
        if (leer(h)) speicher.removeItem(HAEUFIGKEIT_SPIEGEL_SCHLUESSEL);
        else speicher.setItem(HAEUFIGKEIT_SPIEGEL_SCHLUESSEL, JSON.stringify({ nutzerId, knoten: [...h.knoten], kategorien: [...h.kategorien] }));
    } catch {
        // Nur Rückfall für offline.
    }
}

/**
 * Nach dem Login: erst der Spiegel (sofort, auch offline), dann der frische
 * Stand der RPC. `nochGueltig` false (Abmelden, neuer Login) → nichts setzen.
 * Best effort, wirft nie.
 */
export async function haeufigkeitAktualisieren(
    nutzerId: string | null,
    nochGueltig: () => boolean,
    abfrage?: HaeufigkeitAbfrage,
    speicherRoh?: Speicher | null,
): Promise<void> {
    try {
        if (!nutzerId) return;
        const speicher = speicherRoh === undefined ? standardSpeicher() : speicherRoh;
        const gespiegelt = haeufigkeitSpiegelLesen(nutzerId, speicher);
        if (gespiegelt && nochGueltig()) setzeKatalogHaeufigkeit(gespiegelt);
        const frisch = await haeufigkeitAbrufen(abfrage);
        if (!frisch || !nochGueltig()) return;
        setzeKatalogHaeufigkeit(frisch);
        haeufigkeitSpiegelSchreiben(nutzerId, frisch, speicher);
    } catch (e) {
        console.warn('Häufigkeit des Mängelkatalogs nicht ladbar', e);
    }
}
